<script setup lang="ts">
/**
 * PayHero — bloc d'en-tête réutilisable de la page /pay/[slug].
 *
 * Rend, selon la présence d'une bannière, soit un bloc texte centré (titre +
 * montant + citation), soit une bannière image floutée
 * avec ces mêmes infos en overlay blanc/sombre. Variante `desktop` ou `mobile` pour
 * adapter les tailles (image, polices, paddings).
 *
 * Factorisé hors de /pay/[slug].vue car le bloc était dupliqué 2× (desktop
 * + mobile) avec exactement la même logique mais des classes Tailwind
 * différentes — pénible à maintenir et bruyant à lire.
 */

interface Props {
  bannerUrl: string | null;
  customTitle: string | null;
  /**
   * Description du lien de paiement (colonne `subtitle`, affichée « Sous-titre »
   * au dashboard jusqu'au 2026-10-04). Rendue en citation, repliée avec
   * « Voir plus » ; le message du QR code la remplace s'il existe.
   */
  customSubtitle: string | null;
  /** Montant pré-formatté (ex. "12 €" ou "—"). */
  displayAmount: string;
  /** Don récurrent ? Active le suffixe "/semaine|mois|an". */
  isSubscription: boolean;
  /** Suffixe d'abonnement pré-calculé ("semaine" | "mois" | "an"). */
  subscriptionSuffix: string;
  /** Nom de l'organisation (alt de la bannière). */
  organizationName: string;
  /**
   * Message personnalisé écrit par l'association sur le QR CODE. Rendu comme
   * une CITATION — guillemet ouvrant, italique — et non comme un sous-titre :
   * c'est un mot adressé au donateur, pas une ligne d'interface.
   */
  message: string | null;
  /** Variante d'affichage — diffère par tailles & paddings uniquement. */
  variant: "desktop" | "mobile";
}

const props = defineProps<Props>();

const isDesktop = computed(() => props.variant === "desktop");

/**
 * UN seul texte sous le montant, en citation (guillemet, « Voir plus ») : le
 * message du QR code s'il y en a un, sinon la description du lien de
 * paiement. Les deux à la fois faisaient doublon sur la page d'un QR code —
 * souvent le même texte, recopié (Karim 2026-10-04).
 */
const citation = computed(
  () => props.message?.trim() || props.customSubtitle?.trim() || null,
);
</script>

<template>
  <!-- Variante texte centré (pas de bannière) -->
  <div
    v-if="!bannerUrl"
    :class="
      isDesktop
        ? 'my-12'
        : 'flex flex-col justify-center items-center text-center py-8'
    "
  >
    <p
      v-if="customTitle"
      :class="
        isDesktop
          ? 'text-lg font-medium text-gray-600 mb-4'
          : 'text-base font-medium text-gray-600 mb-2'
      "
    >
      {{ customTitle }}
    </p>
    <div
      :class="
        isDesktop
          ? 'text-4xl font-bold text-gray-900'
          : 'text-4xl font-bold text-gray-900 my-2'
      "
    >
      {{ displayAmount }}
      <span
        v-if="isSubscription"
        class="text-base font-normal text-gray-500"
      >/{{ subscriptionSuffix }}</span>
    </div>
    <!-- Message du QR code ou description du lien, replié + « Voir plus ». -->
    <PayHeroMessage
      v-if="citation"
      :message="citation"
      tone="plain"
      :is-desktop="isDesktop"
    />
  </div>

  <!-- Variante bannière image avec overlay sombre.
       C'est le CONTENU qui donne la hauteur (avec un minimum), l'image n'est
       qu'un fond : avant, l'image fixait la hauteur et le texte était posé
       par-dessus en `absolute` — un message long débordait et recouvrait le
       montant au lieu d'agrandir la bannière (Karim 2026-10-04). -->
  <div
    v-else
    :class="
      isDesktop
        ? 'relative rounded-[40px] overflow-hidden shadow-lg mb-6 bg-black'
        : 'relative rounded-[40px] overflow-hidden shadow-lg bg-black'
    "
  >
    <img
      :src="bannerUrl"
      :alt="organizationName"
      class="absolute inset-0 w-full h-full object-cover blur-xs scale-110"
    />
    <div class="absolute inset-0 bg-black/60" />
    <div
      :class="
        isDesktop
          ? 'relative min-h-56 flex flex-col justify-center items-center text-white p-6 gap-4'
          : 'relative min-h-48 flex flex-col justify-center items-center text-white p-4 gap-3'
      "
    >
      <p
        v-if="customTitle"
        :class="
          isDesktop
            ? 'text-center text-lg font-medium text-white/90'
            : 'text-center text-base font-medium text-white/90'
        "
      >
        {{ customTitle }}
      </p>
      <div :class="isDesktop ? 'text-5xl font-bold' : 'text-4xl font-bold'">
        {{ displayAmount }}
        <span
          v-if="isSubscription"
          :class="
            isDesktop
              ? 'text-lg font-normal text-white/80'
              : 'text-base font-normal text-white/80'
          "
        >/{{ subscriptionSuffix }}</span>
      </div>
      <!-- Même citation, en blanc sur la bannière assombrie. -->
      <PayHeroMessage
        v-if="citation"
        :message="citation"
        tone="banner"
        :is-desktop="isDesktop"
      />
    </div>
  </div>
</template>
